/**
 * FlowAI Approval-Inbox — alle Pending-Actions zur Freigabe.
 *
 * @page /flowai/approvals
 * @since v2.484.0
 */
import { useEffect, useState } from 'react';
import { PageHeader, Card, Button, Badge } from '../../components/ui';
import { IconCases } from '../../components/Icons';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

export default function FlowAIApprovalInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  async function load() {
    setLoading(true);
    const r = await fetch(`${API}/api/flowai/actions/pending`, { credentials: 'include' });
    const data = await r.json().catch(() => ({}));
    setItems(Array.isArray(data) ? data : (data?.actions ?? []));
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function openItem(it) {
    setActive(it);
    setEditMode(false);
    setEditSubject(it.action_payload?.subject || '');
    setEditBody(it.action_payload?.body_md || '');
    setRejectReason('');
  }

  async function approve(it) {
    setBusy(true);
    await fetch(`${API}/api/flowai/actions/${it.id}/approve`, { method: 'POST', credentials: 'include' });
    setBusy(false);
    setActive(null);
    load();
  }
  async function editApprove(it) {
    setBusy(true);
    await fetch(`${API}/api/flowai/actions/${it.id}/edit-approve`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject: editSubject, body_md: editBody }),
    });
    setBusy(false);
    setActive(null);
    load();
  }
  async function reject(it) {
    if (!rejectReason.trim()) { alert('Bitte Grund angeben'); return; }
    setBusy(true);
    await fetch(`${API}/api/flowai/actions/${it.id}/reject`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: rejectReason }),
    });
    setBusy(false);
    setActive(null);
    load();
  }

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[{ label: 'Approval-Inbox' }]} />
      <PageHeader
        title="Approval-Inbox"
        subtitle="Offene FlowAI-Aktionen die Freigabe benötigen"
        actions={<Button onClick={load} variant="secondary">Aktualisieren</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        <Card padding="sm" title={`${items.length} offen`}>
          {loading ? (
            <div className="p-4 text-sm">Lade…</div>
          ) : (
            <div className="divide-y divide-[var(--border-subtle,#E5E7EB)] max-h-[70vh] overflow-y-auto">
              {items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => openItem(it)}
                  className={`w-full text-left p-3 text-sm hover:bg-[var(--bg-inset,#F8F9FC)] ${active?.id === it.id ? 'bg-[var(--bg-inset,#F8F9FC)]' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[var(--text-tertiary,#6B7280)]">#{it.id}</span>
                    <Badge>{it.action_type}</Badge>
                    {it.related_case_id && <Badge variant="default">Case #{it.related_case_id}</Badge>}
                  </div>
                  <div className="text-[12px] font-medium line-clamp-1">{it.action_payload?.subject || it.action_payload?.to || '(ohne Subject)'}</div>
                  <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-1">{new Date(it.created_at).toLocaleString()}</div>
                </button>
              ))}
              {items.length === 0 && (
                <div className="p-12 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--bg-inset,#F8F9FC)] mb-3">
                    <IconCases className="w-6 h-6 text-[var(--text-tertiary,#6B7280)]" />
                  </div>
                  <div className="text-sm font-medium mb-1">Keine offenen Aktionen</div>
                  <div className="text-[12px] text-[var(--text-tertiary,#6B7280)]">Alle FlowAI-Aktionen sind freigegeben oder abgelehnt.</div>
                </div>
              )}
            </div>
          )}
        </Card>

        <Card>
          {!active ? (
            <div className="p-6 text-center text-sm text-[var(--text-tertiary,#6B7280)]">Wähle eine Aktion aus der Liste.</div>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="font-mono text-[var(--text-tertiary,#6B7280)]">#{active.id}</span>
                <Badge>{active.action_type}</Badge>
                {active.related_case_id && <Badge variant="default">Case #{active.related_case_id}</Badge>}
                <span className="ml-auto text-[11px] text-[var(--text-tertiary,#6B7280)]">{new Date(active.created_at).toLocaleString()}</span>
              </div>
              <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mb-2">Empfänger: <b>{active.action_payload?.to}</b></div>

              {!editMode ? (
                <>
                  <div className="text-sm font-medium mb-2">{active.action_payload?.subject}</div>
                  <div className="bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-subtle,#E5E7EB)] rounded p-4 text-[13px] whitespace-pre-wrap max-h-[400px] overflow-y-auto">
                    {active.action_payload?.body_md || '(kein Body)'}
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} className="w-full px-3 py-2 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm font-medium" />
                  <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} className="w-full px-3 py-2 border border-[var(--border-subtle,#E5E7EB)] rounded text-[13px] font-mono min-h-[300px]" />
                </div>
              )}

              <div className="mt-4 flex gap-2 items-center">
                {!editMode ? (
                  <>
                    <Button onClick={() => approve(active)} disabled={busy}>Freigeben</Button>
                    <Button variant="secondary" onClick={() => setEditMode(true)}>Bearbeiten & freigeben</Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => editApprove(active)} disabled={busy}>Speichern & freigeben</Button>
                    <Button variant="ghost" onClick={() => setEditMode(false)}>Abbrechen</Button>
                  </>
                )}
                <div className="flex-1" />
                <input
                  placeholder="Ablehnungsgrund"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="px-3 py-2 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm w-[260px]"
                />
                <Button variant="danger" onClick={() => reject(active)} disabled={busy}>Ablehnen</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
